import React from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { getThemeClasses } from '../../utils/themeClasses';

export interface InputProps {
  label?: string;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  multiline?: boolean;
  rows?: number;
  margin?: 'none' | 'dense' | 'normal';
  variant?: 'outlined' | 'filled' | 'standard';
  InputProps?: {
    startAdornment?: React.ReactNode;
    endAdornment?: React.ReactNode;
  };
  className?: string;
  disabled?: boolean;
  required?: boolean;
  placeholder?: string;
  value?: string | number;
  defaultValue?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  type?: string;
  name?: string;
  id?: string;
  autoFocus?: boolean;
}

const Input = React.forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps>(
  (
    {
      label,
      error = false,
      helperText,
      fullWidth = false,
      multiline = false,
      rows = 3,
      margin = 'none',
      variant = 'outlined',
      className = '',
      InputProps,
      ...props
    },
    ref
  ) => {
    const { mode } = useTheme();
    const theme = getThemeClasses(mode);
    
    const marginClasses = {
      none: '',
      dense: 'mt-1',
      normal: 'mt-2',
    };

    const baseInputClasses = `w-full rounded-full border ${theme.bg.input} ${theme.text.primary} transition-colors focus:outline-none focus:ring-2`;
    const errorClasses = error
      ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
      : `${theme.border.input} focus:border-card-border focus:ring-[#6f4e37]/30`;
    
    const paddingClasses = multiline ? 'px-4 py-3 rounded-2xl' : 'px-4 py-3';

    const inputClasses = [
      baseInputClasses,
      errorClasses,
      paddingClasses,
      props.disabled ? `opacity-60 cursor-not-allowed ${mode === 'light' ? 'bg-gray-100' : 'bg-gray-900'}` : '',
      className,
    ].filter(Boolean).join(' ');

    const InputElement = multiline ? 'textarea' : 'input';

    return (
      <div className={`${fullWidth ? 'w-full' : ''} ${marginClasses[margin]}`}>
        {label && (
          <label className={`block text-sm font-medium mb-1 ${error ? 'text-red-500' : theme.text.secondary}`}>
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          {InputProps?.startAdornment && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center">
              {InputProps.startAdornment}
            </div>
          )}
          <InputElement
            ref={ref as any}
            className={`${inputClasses} ${InputProps?.startAdornment ? 'pl-10' : ''} ${InputProps?.endAdornment ? 'pr-10' : ''}`}
            rows={multiline ? rows : undefined}
            {...props}
          />
          {InputProps?.endAdornment && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center">
              {InputProps.endAdornment}
            </div>
          )}
        </div>
        {helperText && (
          <p className={`mt-1 text-sm ${error ? 'text-red-500' : 'text-gray-400'}`}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
